import { useCallback, useContext, useMemo, useRef } from 'react';

import type { ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import type { ExperienceName } from '@confluence/experience-tracker/entry-points/ExperienceNameType.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';

import { getApprovalsWriteAbortReason } from './approvalsExpectedErrors.ts';

export type ApprovalsWriteExperience = {
	startExperience: (attributes?: ExperienceAttributes) => void;
	succeedExperience: () => void;
	failExperience: (error: unknown) => void;
};

/**
 * Shared lifecycle for the approvals mutation experiences. Each experience gets its own
 * named hook wrapping this, so a call site can never advance the wrong one.
 *
 * The gate is read at the start boundary only: once an attempt has started it is always
 * terminated, so a gate value that changes mid-flight cannot leave an orphan start to
 * time out and be miscounted as a failure.
 *
 * No monitoring submits — ApprovalsContext's handleApprovalMutationError has already
 * reported (or deliberately marked as handled) every error that reaches here.
 */
export const useApprovalsWriteExperience = (name: ExperienceName): ApprovalsWriteExperience => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const hasStartedRef = useRef(false);

	const startExperience = useCallback(
		(attributes?: ExperienceAttributes) => {
			if (!fg('confluence_approvals_fe_experience_tracking')) {
				return;
			}

			hasStartedRef.current = true;
			experienceTracker.start({ name, ...(attributes ? { attributes } : {}) });
		},
		[experienceTracker, name],
	);

	const succeedExperience = useCallback(() => {
		if (!hasStartedRef.current) {
			return;
		}

		hasStartedRef.current = false;
		experienceTracker.succeed({ name });
	}, [experienceTracker, name]);

	const failExperience = useCallback(
		(error: unknown) => {
			if (!hasStartedRef.current) {
				return;
			}

			hasStartedRef.current = false;
			const abortReason = getApprovalsWriteAbortReason(error);

			if (abortReason) {
				experienceTracker.abort({ name, reason: abortReason, checkForTimeout: false });
				return;
			}

			experienceTracker.stopOnError({
				name,
				error: error instanceof Error ? error : new Error(String(error)),
			});
		},
		[experienceTracker, name],
	);

	return useMemo(
		() => ({ startExperience, succeedExperience, failExperience }),
		[startExperience, succeedExperience, failExperience],
	);
};
